import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PrintReport.module.css';

function fmt(n) {
  if (!n && n !== 0) return 'N/A';
  const abs = Math.abs(n);
  if (abs >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (abs >= 1e9)  return '$' + (n / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6)  return '$' + (n / 1e6).toFixed(2) + 'M';
  return '$' + n.toLocaleString();
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return iso; }
}

function fmtPeriod(dateStr) {
  if (!dateStr || dateStr === 'N/A') return null;
  try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); }
  catch { return dateStr; }
}

export default function PrintReport({ data }) {
  const { t } = useTranslation();
  if (!data || data.type !== 'stock') return null;

  const { financials: f, analysis: a, historicalTrend, standards } = data;
  const hasTrend = Array.isArray(historicalTrend) && historicalTrend.length >= 2;
  const today = fmtDate(new Date().toISOString());

  return (
    <div className="shariah-print">
      <div className={styles.report}>

        {/* ── Page header ── */}
        <div className={styles.pageHeader}>
          <div>
            <p className={styles.reportTitle}>{t('print_report.title')}</p>
            <p className={styles.reportSub}>{t('print_report.subtitle')}</p>
          </div>
          <p className={styles.reportDate}>{today}</p>
        </div>

        <div className={styles.rule} />

        {/* ── Stock identity + verdict ── */}
        <div className={styles.identity}>
          <div className={styles.identityLeft}>
            <h1 className={styles.ticker}>{f.ticker}</h1>
            <p className={styles.company}>{f.companyName}</p>
            <p className={styles.identityMeta}>
              {[f.exchange, f.sector, f.industry].filter(Boolean).join(' · ')}
            </p>
            {fmtPeriod(f.period) && (
              <p className={styles.identityMeta}>{t('print_report.annual_filing')} {fmtPeriod(f.period)}</p>
            )}
          </div>
          <div className={styles.verdictTiles}>
            {[
              { label: 'AAOIFI', verdict: standards?.aaoifi?.verdict || a.overallVerdict },
              { label: 'DJIM',   verdict: standards?.djim?.verdict   || a.overallVerdict },
              { label: 'MSCI',   verdict: standards?.msci?.verdict   || a.overallVerdict },
            ].map(({ label, verdict }) => (
              <div key={label} className={`${styles.verdictBlock} ${styles['verdict_' + verdict]}`}>
                <p className={styles.verdictLabel}>{label}</p>
                <p className={styles.verdictValue}>{t(`print_report.verdict.${verdict}`) || verdict}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Financial summary ── */}
        <div className={styles.financialRow}>
          {[
            { label: t('print_report.financial.revenue'),      value: fmt(f.revenue) },
            { label: t('print_report.financial.total_assets'), value: fmt(f.totalAssets) },
            { label: t('print_report.financial.total_debt'),   value: fmt(f.totalDebt) },
            { label: t('print_report.financial.market_cap'),   value: fmt(f.marketCap) },
          ].map(({ label, value }) => (
            <div key={label} className={styles.financialCell}>
              <p className={styles.financialLabel}>{label}</p>
              <p className={styles.financialValue}>{value}</p>
            </div>
          ))}
        </div>

        <div className={styles.rule} />

        {/* ── Criteria table ── */}
        <p className={styles.sectionTitle}>{t('print_report.section.five_criteria')}</p>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thCriterion}>{t('print_report.table.criterion')}</th>
              <th className={styles.thValue}>{t('print_report.table.value')}</th>
              <th className={styles.thThreshold}>{t('print_report.table.threshold')}</th>
              <th className={styles.thResult}>{t('print_report.table.result')}</th>
            </tr>
          </thead>
          <tbody>
            {(a.summaryRows || []).map((row, i) => (
              <tr key={i} className={styles.tr}>
                <td className={styles.tdCriterion}>
                  <span className={styles.criterionNum}>0{i + 1}</span> {row.criterion}
                </td>
                <td className={styles.tdValue}>{row.value}</td>
                <td className={styles.tdThreshold}>{row.threshold}</td>
                <td className={`${styles.tdResult} ${styles['status_' + row.status]}`}>
                  {t(`print_report.status.${row.status}`)} {row.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Purification ── */}
        {a.requiresPurification && a.purificationPct != null && (
          <div className={styles.purifyBox}>
            <p className={styles.purifyTitle}>{t('print_report.purification.title')}</p>
            <p className={styles.purifyText} dangerouslySetInnerHTML={{ __html: t('print_report.purification.text', { pct: a.purificationPct }) }} />
          </div>
        )}

        {/* ── Non-compliant factors ── */}
        {a.nonCompliantFactors && (
          <div className={styles.failBox}>
            <p className={styles.failTitle}>{t('print_report.non_compliant.title')}</p>
            <p className={styles.failText}>{a.nonCompliantFactors}</p>
          </div>
        )}

        {/* ── Assessment ── */}
        {a.summaryNote && (
          <>
            <div className={styles.rule} />
            <p className={styles.sectionTitle}>{t('print_report.section.assessment')}</p>
            <p className={styles.assessmentText}>{a.summaryNote}</p>
          </>
        )}

        {/* ── Historical trend — cap at 3 years in print to stay on one page ── */}
        {hasTrend && (
          <>
            <div className={styles.rule} />
            <p className={styles.sectionTitle}>{t('print_report.section.historical_trend')}</p>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.thValue}>{t('print_report.trend_table.year')}</th>
                  <th className={styles.thValue}>{t('print_report.trend_table.verdict')}</th>
                  <th className={styles.thValue}>{t('print_report.trend_table.debt_assets')}</th>
                  <th className={styles.thValue}>{t('print_report.trend_table.haram_rev')}</th>
                  <th className={styles.thValue}>{t('print_report.trend_table.illiquid_assets')}</th>
                </tr>
              </thead>
              <tbody>
                {historicalTrend.slice(0, 3).map(row => (
                  <tr key={row.period} className={styles.tr}>
                    <td className={styles.tdValue}>
                      {row.year}{row.isCurrent ? ' ●' : ''}
                    </td>
                    <td className={`${styles.tdValue} ${styles['verdict_inline_' + row.overallVerdict]}`}>
                      {t(`print_report.verdict.${row.overallVerdict}`)}
                    </td>
                    <td className={`${styles.tdValue} ${styles['status_' + row.debtStatus]}`}>
                      {row.debtRatio != null ? row.debtRatio + '%' : '—'}
                    </td>
                    <td className={`${styles.tdValue} ${styles['status_' + row.haramStatus]}`}>
                      {row.haramRatio != null ? row.haramRatio + '%' : '—'}
                    </td>
                    <td className={`${styles.tdValue} ${styles['status_' + row.illiquidStatus]}`}>
                      {row.illiquidRatio != null ? row.illiquidRatio + '%' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className={styles.trendNote}>{t('print_report.trend_note')}</p>
          </>
        )}

        {/* ── Footer ── */}
        <div className={styles.footer}>
          <div className={styles.rule} />
          <p className={styles.disclaimer}>{t('print_report.disclaimer')}</p>
          <div className={styles.footerRow}>
            <p className={styles.footerBrand}>{t('print_report.footer_brand')}</p>
            <p className={styles.footerBrand}>{t('print_report.generated', { date: today })}</p>
          </div>
        </div>

      </div>
    </div>
  );
}
